import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Copy, RefreshCw, Search, X } from 'lucide-react';
import { Action, ErrorNote, Input, Loading, Panel } from '../../components/aa';
import { adoptionApi, errorText, plural } from './adoption';
import type { CompanyGraph } from './adoption';
import { GraphMap, KIND_COLOR } from './GraphMap';

const KINDS: { kind: string; one: string; many: string }[] = [
  { kind: 'person', one: 'person', many: 'people' },
  { kind: 'department', one: 'department', many: 'departments' },
  { kind: 'tool', one: 'tool', many: 'tools' },
  { kind: 'opportunity', one: 'opportunity', many: 'opportunities' },
  { kind: 'session', one: 'session', many: 'sessions' },
  { kind: 'project', one: 'project', many: 'projects' },
  { kind: 'routine', one: 'routine', many: 'routines' },
  { kind: 'workflow', one: 'workflow', many: 'workflows' },
  { kind: 'skill', one: 'skill', many: 'skills' },
];

const updated = (iso: string | null): string => {
  if (!iso) return 'Not built yet';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return 'Updated just now';
  if (minutes < 60) return `Updated ${plural(minutes, 'minute', 'minutes')} ago`;
  return `Updated ${new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`;
};

export const GraphPanel: React.FC<{ companyId: string; companyName: string }> = ({ companyId, companyName }) => {
  const [graph, setGraph] = useState<CompanyGraph | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState('');
  const [shownFocus, setShownFocus] = useState('');

  const latest = useRef(0);

  const load = useCallback(async (topic: string) => {
    latest.current += 1;
    const mine = latest.current;
    setBusy(true);
    setError('');
    try {
      const { data } = await adoptionApi.graph(companyId, topic);
      if (mine !== latest.current) return;
      setGraph(data);
      setShownFocus(topic);
    } catch (err) {
      if (mine === latest.current) setError(errorText(err, 'Could not load the knowledge graph.'));
    } finally {
      if (mine === latest.current) setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    const topic = focus.trim();
    const timer = window.setTimeout(() => load(topic), 350);
    return () => window.clearTimeout(timer);
  }, [focus, load]);

  const rebuild = async () => {
    setBusy(true);
    try {
      await adoptionApi.rebuildGraph(companyId);
      await load(shownFocus);
      toast.success('Knowledge graph rebuilt');
    } catch (err) {
      toast.error(errorText(err, 'Could not rebuild the knowledge graph.'));
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!graph) return;
    try {
      await navigator.clipboard.writeText(graph.brief);
      toast.success('Copied');
    } catch {
      toast.info('Select the text and copy it yourself.');
    }
  };

  if (error && !graph) return <ErrorNote message={error} />;
  if (!graph) return <Loading label="Building the knowledge graph" />;

  const chips = KINDS.filter((k) => (graph.counts[k.kind] || 0) > 0);
  let previewNote = 'The full map, as Orbit reads it when a conversation starts.';
  if (shownFocus) previewNote = `As Orbit reads it while a conversation is about "${shownFocus}". Matching lines are highlighted and listed first in each section.`;
  const direct = graph.related.filter((r) => r.direct);
  const linked = graph.related.filter((r) => !r.direct);
  const highlight = new Set(graph.related.map((r) => r.key));
  const directLabels = direct.map((r) => r.label.toLowerCase()).filter((l) => l.length >= 3);
  const matches = (line: string): boolean => {
    const lower = line.toLowerCase();
    return directLabels.some((l) => lower.includes(l));
  };

  return (
    <div className="space-y-4">
      <Panel>
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-5 sm:px-6">
          <div className="min-w-0 max-w-[68ch]">
            <h3 className="m-0 text-[16px] font-bold text-ink">Knowledge graph</h3>
            <p className="m-0 mt-1 text-[13px] leading-[20px] text-ink-muted">
              Everything the platform knows about {companyName}, linked together: who works where, which tools are connected and which are only used, who raised each opportunity, what it became, and what is running. Orbit reads this map, so it never asks for what is already known.
            </p>
            <p className="m-0 mt-2 text-[12px] text-ink-dim">{updated(graph.built_at)} · kept up to date automatically</p>
          </div>
          <Action variant="quiet" size="sm" icon={<RefreshCw size={14} />} onClick={rebuild} disabled={busy} reason="Working">Rebuild now</Action>
        </div>
        <div className="flex flex-wrap gap-2 border-t border-line-soft px-5 py-4 sm:px-6">
          {chips.map((k) => (
            <span key={k.kind} className="rounded-full bg-canvas-inset px-3 py-1.5 text-[12.5px] font-semibold text-ink">
              {plural(graph.counts[k.kind], k.one, k.many)}
            </span>
          ))}
          <span className="rounded-full bg-ink px-3 py-1.5 text-[12.5px] font-semibold text-white">{plural(graph.links, 'link', 'links')}</span>
        </div>
      </Panel>
      <Panel>
        <div className="px-5 pb-4 pt-5 sm:px-6">
          <h3 className="m-0 text-[15px] font-bold text-ink">Map</h3>
          <p className="m-0 mt-1 text-[12.5px] leading-[19px] text-ink-dim">Every dot is something the platform knows about this company, and every line is how two of them connect. Type a topic to find what relates to it.</p>
          <div className="relative mt-3 max-w-[560px]">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" aria-hidden />
            <Input
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              placeholder="Find what relates to a topic, for example: invoices, HubSpot, sales"
              className="!pl-9 !pr-10"
              aria-label="Find what relates to a topic"
            />
            {focus && (
              <button type="button" onClick={() => setFocus('')} aria-label="Clear the topic" className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-lg text-ink-dim hover:bg-canvas-inset hover:text-ink">
                <X size={15} aria-hidden />
              </button>
            )}
          </div>
          {shownFocus && (
            <div className="mt-3" aria-live="polite">
              {graph.related.length === 0 && (
                <p className="m-0 text-[12.5px] text-ink-muted">Nothing in the map relates to &ldquo;{shownFocus}&rdquo; yet. Try a tool, a team or a task.</p>
              )}
              {graph.related.length > 0 && (
                <div>
                  <p className="m-0 text-[12.5px] text-ink-muted">
                    <b className="text-ink">{plural(direct.length, 'thing matches', 'things match')}</b> &ldquo;{shownFocus}&rdquo;
                    {linked.length > 0 && `, and ${plural(linked.length, 'more is', 'more are')} linked to them`}. They are lit up on the map.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {direct.map((r) => (
                      <span key={r.key} className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-white px-2.5 py-1 text-[12px] font-semibold text-ink">
                        <span className="h-2 w-2 rounded-full" style={{ background: KIND_COLOR(r.kind) }} aria-hidden />
                        {r.label}
                      </span>
                    ))}
                    {linked.slice(0, 12).map((r) => (
                      <span key={r.key} className="inline-flex items-center gap-1.5 rounded-full bg-canvas-inset px-2.5 py-1 text-[12px] text-ink-muted">
                        <span className="h-2 w-2 rounded-full" style={{ background: KIND_COLOR(r.kind) }} aria-hidden />
                        {r.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <GraphMap nodes={graph.nodes} edges={graph.edges} highlight={highlight} />
      </Panel>
      <Panel>
        <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 sm:px-6">
          <div className="min-w-0">
            <h3 className="m-0 text-[15px] font-bold text-ink">What the AI reads</h3>
            <p className="m-0 mt-1 text-[12.5px] leading-[19px] text-ink-dim">{previewNote}</p>
          </div>
          <Action size="sm" variant="ghost" icon={<Copy size={14} />} onClick={copy}>Copy</Action>
        </div>
        <pre className="m-0 mt-4 max-h-[560px] overflow-auto whitespace-pre-wrap border-t border-line-soft bg-canvas px-5 py-4 font-mono text-[12px] leading-[19px] text-ink sm:px-6" aria-busy={busy}>
          {graph.brief.split('\n').map((line, i) => {
            const key = `${i}-${line.slice(0, 12)}`;
            if (shownFocus && matches(line)) return <mark key={key} className="block rounded bg-accent-100 text-ink">{line}</mark>;
            return <span key={key} className="block">{line || ' '}</span>;
          })}
        </pre>
      </Panel>
    </div>
  );
};

export default GraphPanel;
